// Runtime-iterable permission catalog. Permissions are defined by code (the
// capabilities endpoints expose), NOT by users — so they live here, not in a
// DB table. RolePermission.permissionKey must be a member of PERMISSION_KEYS
// (asserted at API boot). Convention: '<resource>.<action>'.
export const PERMISSION_KEYS = [
    // admin / rbac
    'role.manage',
    'user.role.assign',
    // user lifecycle management (create/provision, deactivate, edit companies) —
    // distinct from 'user.role.assign' (which only swaps a user's company-scoped roles).
    'user.manage',
    'approval.manage',
    // M7 Phase 3 — SoD override (service-layer; PermissionResolver.resolve +
    // hasPermission). Senior-only escape valve for the single-qualified-approver
    // deadlock. In the catalog (not via @RequirePermission) so boot + seed pass.
    'approval.sodoverride',
    // journal
    'journal.read',
    'journal.create',
    'journal.update',
    'journal.submit',
    'journal.approve',
    'journal.reject',
    'journal.return',
    'journal.post',
    'journal.delete',
    // payment requests (all endpoints exist today)
    'payreq.read',
    'payreq.create',
    'payreq.submit',
    'payreq.approve',
    'payreq.reject',
    'payreq.return',
    'payreq.pay',
    'payreq.cancel',
    // AP bills (manual + central-engine approval flow)
    'apbill.create',
    'apbill.approve',
    // employee advances (create/submit + engine approval + disburse + clear)
    'advance.create',
    'advance.approve',
    'advance.disburse',
    'advance.clear',
    // M4 — upload/download clearance receipts (MinIO + Claude OCR prefill)
    'advance.receipt',
    // M4 — send an overdue-advance reminder (manual button + BullMQ dunning cron)
    'advance.remind',
    // AP cross-vendor payment-run (engine approval) — create/submit (run) + approve (Phase B B2)
    'appayment.run',
    // AP single-bill payment release (M7 1b, engine APV 4-eyes) — create/submit (maker);
    // approve/reject/return REUSE the existing 'appayment.approve' below.
    'appayment.create',
    'appayment.approve',
    // AP credit/debit notes (ใบลดหนี้/ใบเพิ่มหนี้) — create (maker) + central-engine approval (approver)
    'apcreditnote.create',
    'apcreditnote.approve',
    // AP payment-term master (เงื่อนไขชำระเงิน) CRUD (AP Phase C1)
    'paymentterm.manage',
    // AP recurring-bill templates (แม่แบบบิลประจำ) CRUD + manual generate (AP Phase C4)
    'recurringbill.manage',
    // petty cash (catalog was missing these — granted in seed + used by
    // petty-cash.controller; pre-existing main gap patched to unblock seed/boot)
    'pettycash.read',
    'pettycash.create',
    'pettycash.replenish',
    // M3 maker-checker: submit (maker) + approve/reject/return (checker, via engine)
    'pettycash.submit',
    'pettycash.approve',
    // M4 — cash count (นับเงินสด) maker-checker (docType PCC): read/create (maker
    // ACCOUNTANT) + approve (checker, via engine FINANCE_MANAGER)
    'pettycash.count.read',
    'pettycash.count.create',
    'pettycash.count.approve',
    // M4 — float close (ปิดวงเงิน) maker-checker (docType PCX): read/create (maker
    // ACCOUNTANT) + approve (checker, via engine FINANCE_MANAGER→CFO 2-step)
    'pettycash.close.read',
    'pettycash.close.create',
    'pettycash.close.approve',
    // doc ingest (AI document → ApBill: upload + review accept/reject)
    'docIngest.create',
    // emp benefit (TAS 19)
    'empben.read',
    'empben.valuation.create',
    'empben.valuation.adopt',
    'empben.monthly.run',
    'empben.settlement.create',
    'empben.remeasurement.create',
    'empben.approve',
    'empben.reject',
    'empben.return',
    'empben.reverse',
    // Email intake (M4): settings — routes/allowlist CRUD + mailbox status +
    // quarantine release/discard. Ingested documents themselves keep docIngest.create.
    'emailintake.manage',
    // Scan intake (M5): settings — routes (prefix→company) CRUD + scan status +
    // quarantine release/discard. Ingested documents themselves keep docIngest.create.
    'scanintake.manage',
    // Line intake (M6): settings — channels (destination→company) + sender
    // allowlist CRUD + line status + quarantine release/discard. Ingested
    // documents themselves keep docIngest.create.
    'lineintake.manage',
    // suppliers (M2-c master-data CRUD + RD re-verify)
    'supplier.read',
    'supplier.create',
    'supplier.update',
    'supplier.verify',
    // M7 Phase 2 (V15) — vendor bank-change 4-eyes (docType VBC). request = maker,
    // approve = the finance approver pool (also confirms the out-of-band attestation).
    'vendorbank.request',
    'vendorbank.approve',
    // M7 Phase 4 — detective anti-fraud reports + case tracking (read-only origin,
    // INFO-only). read = view dashboard/reports; manage = resolve/dismiss + run scan.
    'fraud.read',
    'fraud.manage',
    // low-value asset register (lifecycle actions; markLost/dispose carry SoD)
    'lva.read',
    'lva.create',
    'lva.update',
    'lva.printLabel',
    'lva.transfer',
    'lva.reassign',
    'lva.markDamaged',
    'lva.markLost',
    'lva.dispose',
    // LVA Phase C — physical count
    'lva.count.create',
    'lva.count.perform',
    'lva.count.approve',
    // AR (Accounts Receivable) — invoices, receipts, aging, credit notes
    'ar.invoice.read',
    'ar.invoice.create',
    // Override the customer credit-limit hold when creating an invoice (audited).
    'ar.invoice.creditoverride',
    'ar.receipt.read',
    'ar.receipt.create',
    'ar.aging.read',
    // AR credit notes — create (maker) + central-engine approval (approver)
    'ar.creditnote.create',
    'ar.creditnote.approve',
    // AR invoice void — request (maker) + central-engine approval (approver)
    'ar.invoice.void',
    'ar.invoice.void.approve',
    // AR billing notes (ใบวางบิล) — RBAC-only, no approval engine
    'ar.billingnote.read',
    'ar.billingnote.create',
    'ar.billingnote.cancel',
    // VAT write-off (expired deferred input VAT) — request (maker) +
    // central-engine approval (approver), docType 'VW'
    'vat.writeoff.create',
    'vat.writeoff.approve',
    // AR post-dated cheque register (เช็คลงวันที่ล่วงหน้า) — RBAC-only, no engine;
    // manage = deposit/clear/bounce
    'ar.pdc.read',
    'ar.pdc.create',
    'ar.pdc.manage',
    // AR customer advances / deposits (เงินรับล่วงหน้า/มัดจำ) — RBAC-only, no
    // engine; manage = apply (ตัดกับใบแจ้งหนี้) + refund (คืนเงิน)
    'ar.advance.read',
    'ar.advance.create',
    'ar.advance.manage',
    // Prepaid (ค่าใช้จ่ายจ่ายล่วงหน้า) — run governance (engine docType 'PPR') +
    // direct-posting lifecycle verbs. run.approve also gates reject/return (SoD via
    // engine); reverse is the privileged FB08 unwind. submit = maker.
    'prepaid.run.submit',
    'prepaid.run.approve',
    'prepaid.run.reverse',
    'prepaid.terminate',
    'prepaid.modify',
    'prepaid.suspend',
    'prepaid.expensedirect',
    // Evidence binding (§9.5). manage = upload + delete supporting documents that
    // satisfy the required-before-first-run gate. Read/download stay open (no base
    // prepaid read key); delete is soft + audited so stamped evidence is traceable.
    'prepaid.attachment.manage',
    // CIP / งานระหว่างก่อสร้าง (TFRS 23). Every money-path is gated; the central
    // approval-engine SoD on capitalize + borrowing runs stays as defense-in-depth.
    // read = headers/cost-entries/retention/requests/runs + rollforward & disclosure
    // reports. project.create = register a project (no JV). costentry.create = add a
    // cost entry (posts Dr 1240). capitalize.submit = maker submits CIP→FA;
    // capitalize.approve gates approve/reject/return of capitalize requests.
    // borrowing.run = preview + run the monthly TFRS 23 allocation (maker);
    // borrowing.approve gates approve/reject/return of borrowing runs. writeoff =
    // write off an abandoned CIP. retention.release = release contractor retention.
    // lifecycle.manage = mark-ready / suspend / resume / cancel. clearing.receive
    // = receive an AP-bill CIP_CLEARING line into a project (Phase 4b; maker key,
    // granted alongside costentry.create).
    'cip.project.read',
    'cip.project.create',
    'cip.costentry.create',
    'cip.clearing.receive',
    'cip.capitalize.submit',
    'cip.capitalize.approve',
    'cip.borrowing.run',
    'cip.borrowing.approve',
    'cip.writeoff',
    'cip.retention.release',
    'cip.lifecycle.manage',
    // ==========================================================================
    // RBAC hardening (2026-06-17) — close the PermissionsGuard fail-open holes.
    // Every mutating controller endpoint that previously carried NO
    // @RequirePermission now carries one of these keys. Seed grants are layered in
    // prisma/seed.ts (RBAC_HARDENING_ROLE_GRANTS), mirroring the closest existing
    // key's grant. Lanes: maker/master-data -> ACCOUNTANT; approver -> DEPT_MANAGER
    // + FINANCE_MANAGER + CFO + CEO; GL post -> FINANCE_MANAGER; book-close ->
    // FINANCE_MANAGER + CFO.
    // NOTE: consolidation.* / currency.* are intentionally DEFERRED — those routes
    // have no :companyId, so PermissionResolver resolves only GLOBAL roles, making
    // any scoped grant unreachable (admin-only). Handled as a separate decision.
    // accruals
    'accrual.template.manage',
    'accrual.run',
    'accrual.reverse',
    // employee advances — write-off an unrecoverable balance
    'advance.writeoff',
    // fixed-asset masters / categories / lifecycle / attachments
    'assetcategory.manage',
    'assetmaster.manage',
    'fixedasset.create',
    'fixedasset.receive',
    'fixedasset.dispose',
    'fixedasset.depreciation.run',
    'fixedasset.attachment.manage',
    // master data CRUD
    'account-mapping.manage', // ตั้งค่าบัญชีอ้างอิง (logical key → COA code, per company)
    'bankaccount.manage',
    'branch.manage',
    'coa.manage',
    'customer.manage',
    'vendor.manage',
    'department.manage',
    'documenttype.manage',
    'voucherbook.manage',
    'employee.manage',
    'loan.manage',
    'period.manage',
    'project.manage',
    // bank reconciliation (กระทบยอดธนาคาร)
    'bankrecon.manage',
    'bankrecon.finalize',
    // cash-flow forecast
    'cashflow.manage',
    // FX rates + revaluation run/reverse
    'fx.rate.manage',
    'fx.reval.run',
    'fx.reval.reverse',
    // GL-schedule open-item management
    'glsched.account.manage',
    'glsched.manage',
    'glsched.writeoff',
    'glsched.certify',
    'glsched.lock',
    // leases (TFRS 16)
    'lease.create',
    'lease.run',
    'lease.modify',
    'lease.terminate',
    // operating contracts (OSC accrual lifecycle + maker-checker)
    'opcontract.manage',
    'opcontract.submit',
    'opcontract.approve',
    'opcontract.renew',
    'opcontract.terminate',
    'opcontract.rate.manage',
    'opcontract.accrue.run',
    'opcontract.accrue.reverse',
    // period close (book-close control)
    'periodclose.manage',
    // PP&E movement (TAS 16 disclosure)
    'ppemovement.manage',
    // prepaid — record creation + opening balance (modify/run reuse existing keys)
    'prepaid.create',
    'prepaid.openingbalance',
    // property tax (ภาษีโรงเรือน)
    'propertytax.create',
    'propertytax.pay',
    'propertytax.cancel',
    // reclassification entries (create/edit vs GL post are separate lanes)
    'reclass.manage',
    'reclass.post',
    'reclass.reverse',
    // tax adjustments (maker + approver)
    'taxadjustment.manage',
    'taxadjustment.approve',
    // tax reconciliation
    'taxrecon.manage',
    // VAT filings (ภพ.30)
    'vat.filing.manage',
    'vat.filing.create',
    // WHT certificates (50 ทวิ) + filings (ภงด.3/53)
    'wht.cert.manage',
    'wht.remittance.confirm',
    'wht.filing.manage',
];
// Grouped by resource for the admin grid (resource -> keys).
const permissionGroups = {};
for (const key of PERMISSION_KEYS) {
    const resource = key.split('.')[0] ?? 'other';
    (permissionGroups[resource] ??= []).push(key);
}
export const PERMISSION_GROUPS = permissionGroups;
//# sourceMappingURL=permissions.js.map